import PushSettings from '../Push/PushSettings';
import { diagnostic } from '../../utils/diagnostics';
import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { useLocation, useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';

import { useKidAuth } from '../../contexts/KidAuthContext';
import { dbKidData as db } from '../../services/supabase';
import { useBalance } from '../../hooks/useBalance';
import { formatCurrency, parseCurrency } from '../../utils/currency';
import { KID_AVATARS } from '../../utils/constants';

import {
  KID_THEME_LIST,
  getKidTheme,
} from '../../utils/kidThemes';

import {
  getAgeGroupLabel,
} from '../../utils/ageGroups';

import Button from '../UI/Button';
import MyPiggyIcon from '../UI/MyPiggyIcon';
import KidSubscriptionCard from '../Family/KidSubscriptionCard';
import AppDownloadCard from '../Downloads/AppDownloadCard';
import { copyTextToClipboard } from '../../utils/clipboard';
import '../../styles/brand-typography.css';
import './KidSettings.css';


// ==================================================
// Small SVG Icons
// ==================================================

const BackIcon = ({ className = '' }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    aria-hidden="true"
  >
    <path d="M19 12H5" />
    <path d="M11 18l-6-6 6-6" />
  </svg>
);


const LogoutIcon = ({ className = '' }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.6"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    aria-hidden="true"
  >
    <path d="M10 17l5-5-5-5" />
    <path d="M15 12H3" />
    <path d="M14 4h4a3 3 0 0 1 3 3v10a3 3 0 0 1-3 3h-4" />
  </svg>
);


// ==================================================
// Kid Settings Modal
// ==================================================

const KidSettingsModal = ({
  open,
  summary,
  loadingSummary,
  focusApps = false,
  summaryError,
  selectedTheme,
  onSelectTheme,
  selectedAvatar,
  onSelectAvatar,
  onReplayTour,
  onMatchPiggyBank,
  onSave,
  onClose,
  saving,
  error,
  isFamilyInactive,
  isNativeKidApp,
}) => {
  const [copyStatus, setCopyStatus] = useState('');
  const appsRef = useRef(null);
  useEffect(() => {
    if (!open || !focusApps) return;
    const frame = requestAnimationFrame(() => {
      appsRef.current?.scrollIntoView({ block: 'start' });
      appsRef.current?.focus({ preventScroll: true });
    });
    return () => cancelAnimationFrame(frame);
  }, [open, focusApps, loadingSummary]);
  useEffect(() => { if (open) setCopyStatus(''); }, [open]);
  if (!open) return null;

  const child =
    summary?.child || null;

  const adults =
    Array.isArray(
      summary?.adults
    )
      ? summary.adults
      : [];

  const familyName =
    summary?.family
      ?.display_name || '';

  const profileDetails = !loadingSummary && !summaryError && child && (
    <section className="settings-section settings-profile">
      <h3 className="text-lg font-extrabold text-slate-950">My Profile</h3>
      <div className="mt-3 grid grid-cols-1 gap-3">
      <div className="min-w-0 flex items-center gap-3 rounded-lg border border-blue-200 bg-white/80 p-3">
        <span className="shrink-0 text-3xl" aria-hidden="true">{selectedAvatar}</span>
        <div className="min-w-0">
          <p className="text-lg font-extrabold text-slate-950 break-words">{child.nickname || child.full_name || 'My Profile'}</p>
          <p className="text-sm font-bold text-slate-600">{getAgeGroupLabel(child.age_group)}</p>
        </div>
      </div>
      <div className="min-w-0 flex items-center justify-between gap-3 rounded-lg border border-blue-200 bg-white/80 p-3">
        <div className="min-w-0">
          <p className="text-xs font-bold text-slate-600">MyPiggy Login ID</p>
          <p className="font-mono font-extrabold text-purple-800 break-words [overflow-wrap:anywhere]">{child.user_id || 'Not assigned'}</p>
        </div>
        {child.user_id && <Button size="sm" variant="secondary" className="shrink-0" onClick={async () => {
          try { setCopyStatus(await copyTextToClipboard(child.user_id) ? 'Copied!' : 'Unable to copy. Please try again.'); }
          catch { setCopyStatus('Unable to copy. Please try again.'); }
        }}>Copy ID</Button>}
      </div>
      </div>
      {copyStatus && <p role="status" className="mt-1 text-sm font-bold text-teal-800">{copyStatus}</p>}
      <details className="mt-3">
        <summary className="cursor-pointer text-sm font-extrabold text-teal-900">Profile details</summary>
        <p className="mt-2 font-bold text-slate-900">Full Name: {child.full_name || 'Not set'}</p>
        <p className="mt-1 text-sm font-bold text-slate-600">Ask your parent or guardian to change your name, nickname, or age group from Manage Child.</p>
      </details>
    </section>
  );

  const familyDetails = !loadingSummary && !summaryError && child && (
                <section className="settings-section settings-family">
                  <h3 className="font-bold text-gray-900">
                    My Family
                  </h3>
  
                  <div className="mt-3">
                    <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                      Family Name
                    </div>
                    <div className="mt-1 text-lg font-bold text-gray-900 break-words">
                      {familyName
                        ? `${familyName} Family`
                        : 'Family name not set yet'}
                    </div>
                  </div>
  
                  <div className="mt-3 space-y-2">
                    {adults.length > 0 ? (
                      adults.map(
                        (adult, index) => (
                          <div
                            key={`${adult.role}-${adult.name}-${index}`}
                            className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 bg-white px-3 py-2"
                          >
                            <span className="font-semibold text-gray-900 break-words">
                              {adult.name}
                            </span>
  
                            <span className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-bold ${
                              adult.role === 'owner'
                                ? 'bg-blue-100 text-blue-700'
                                : 'bg-violet-100 text-violet-700'
                            }`}>
                              {adult.role === 'owner'
                                ? 'Family Organizer'
                                : 'Parent/Guardian'}
                            </span>
                          </div>
                        )
                      )
                    ) : (
                      <p className="text-sm text-gray-600">
                        Parent and Guardian information is unavailable.
                      </p>
                    )}
                  </div>
                  <details className="mt-3 rounded-lg border border-orange-200 bg-white/70 p-3">
                    <summary className="cursor-pointer font-extrabold text-slate-900">Your Family’s MyPiggy Plan</summary>
                    <div className="mt-3"><KidSubscriptionCard /></div>
                  </details>
                </section>
  );

  const appearanceControls = (
    <div className="space-y-3">
            <section className="settings-section settings-look">
  
              <p className="font-bold text-gray-900">
                My Look
              </p>
  
              <p className="text-sm text-gray-600 mt-1 mb-3">
                Choose your avatar and the theme that feels most like you.
              </p>
  
              <details className="mb-3 rounded-lg border border-purple-200 bg-white/70 px-3 py-2">
                <summary className="cursor-pointer text-sm font-bold text-gray-800">
                  Change Avatar <span className="ml-2 text-xl" aria-hidden="true">{selectedAvatar}</span>
                </summary>
                <div className="mt-3 grid grid-cols-4 gap-2 min-[460px]:grid-cols-5 sm:grid-cols-6">
                {KID_AVATARS.map((avatar) => (
                  <button
                    key={avatar}
                    type="button"
                    onClick={() => onSelectAvatar(avatar)}
                    disabled={saving || isFamilyInactive}
                    aria-label={`Choose ${avatar} avatar`}
                    className={`flex min-h-12 items-center justify-center rounded-xl border-2 p-2 text-2xl transition disabled:opacity-60 ${
                      selectedAvatar === avatar
                        ? 'border-purple-600 bg-purple-50 ring-2 ring-purple-200'
                        : 'border-gray-200 hover:border-purple-300'
                    }`}
                  >
                    {avatar}
                  </button>
                ))}
              </div>
              </details>
  
              <p className="mb-2 text-sm font-bold text-gray-800">
                Dashboard Theme
              </p>
  
  
            <div className="grid grid-cols-2 gap-3">
  
              {KID_THEME_LIST.map((theme) => {
  
                const selected =
                  selectedTheme === theme.id;
  
  
                return (
                  <button
                    key={theme.id}
                    type="button"
                    onClick={() =>
                      onSelectTheme(theme.id)
                    }
                    disabled={
                      saving ||
                      isFamilyInactive
                    }
                    aria-pressed={selected}
                    className={`
                      rounded-xl
                      border-2
                      p-3
                      text-left
                      transition
                      disabled:opacity-60
                      disabled:cursor-not-allowed
  
                      ${
                        selected
  
                          ? `
                            border-gray-900
                            ring-2
                            ring-gray-900/10
                          `
  
                          : `
                            border-gray-200
                            hover:border-gray-400
                          `
                      }
                    `}
                  >
  
                    <div
                      className={`
                        h-6
                        rounded-lg
                        mb-2
                        ${theme.backgroundClass}
                      `}
                    />
  
  
                    <div className="flex items-center justify-between gap-2">
  
                      <span className="font-bold text-gray-900">
                        {theme.emoji} {theme.name}
                      </span>
  
  
                      {selected && (
                        <span
                          className="text-green-600 font-bold"
                          aria-label="Selected"
                        >
                          ✓
                        </span>
                      )}
  
                    </div>
  
  

  
                  </button>
                );
              })}
  
            </div>
  
  
              {isFamilyInactive && (
                <p className="mt-3 text-xs font-semibold text-amber-700">
                  Theme changes are unavailable while your family is read-only. You can still view your profile and family information.
                </p>
              )}
  
            </section>
  
            {error && (
              <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}
  
            <div className="grid grid-cols-2 gap-3">
              <Button
                variant="secondary"
                className="w-full"
                onClick={onClose}
                disabled={saving}
              >
                Cancel
              </Button>
  
              <Button
                className="w-full"
                onClick={onSave}
                disabled={saving || isFamilyInactive}
              >
                {saving ? 'Saving...' : 'Save My Look'}
              </Button>
            </div>
    </div>
  );

  return (
    <div
      className="
        mypiggy-brand kid-settings
        fixed
        inset-0
        z-[100]
        bg-black/45
        flex
        items-center
        justify-center
        px-4
        py-6
      "
      style={isNativeKidApp ? {
        paddingTop: 'calc(env(safe-area-inset-top, 0px) + 1rem)',
        paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 1rem)',
      } : undefined}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="kid-settings-title"
        className="
          w-full
          max-w-xl
          max-h-full
          flex
          flex-col
          rounded-2xl
          bg-white
          shadow-2xl
          overflow-hidden
        "
      >
        <div className="p-4 border-b border-gray-200 shrink-0">

          <div className="flex items-start justify-between gap-4">

            <div>

              <h2
                id="kid-settings-title"
                className="text-xl font-bold text-gray-900"
              >
                <span className="flex items-center gap-2">
                  <MyPiggyIcon name="settings" size="sm" />
                  My Settings
                </span>
              </h2>

              <p className="text-sm text-gray-600 mt-1">
                View your profile and family, or make MyPiggy feel like yours.
              </p>

            </div>


            <button
              type="button"
              onClick={onClose}
              aria-label="Close settings"
              className="
                w-8
                h-8
                rounded-full
                bg-gray-100
                hover:bg-gray-200
                text-gray-700
                flex
                items-center
                justify-center
                font-bold
                transition
              "
            >
              ×
            </button>

          </div>

        </div>


        <div className="p-4 min-h-0 flex-1 overflow-y-auto space-y-4">
          {loadingSummary && (
            <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-5 text-center text-sm text-gray-600">
              Loading your profile and family...
            </div>
          )}


          {summaryError && (
            <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2">
              <p className="text-sm text-red-700">
                {summaryError}
              </p>
            </div>
          )}


          {profileDetails}
          <PushSettings audience="kid" />
          {appearanceControls}

          <section className="settings-section settings-piggy-bank">
            <p className="font-bold text-gray-900">My Piggy Bank</p>
            <p className="mt-1 text-sm text-gray-600">
              Count your real money and make sure MyPiggy shows the same amount.
            </p>
            <Button
              variant="secondary"
              className="mt-3 w-full"
              onClick={onMatchPiggyBank}
              disabled={saving || isFamilyInactive}
            >
              Match My Piggy Bank
            </Button>
          </section>

          {familyDetails}

          <section ref={appsRef} tabIndex={-1} aria-label="MyPiggy Apps" className="settings-section settings-apps">
            <h3 className="text-lg font-extrabold text-slate-950">MyPiggy Apps</h3>
            <p className="mt-1 mb-3 text-sm font-bold text-slate-600">Apps for kids and parents, together in one place.</p>
            <div className="grid grid-cols-1 min-[560px]:grid-cols-2 gap-3">
              <AppDownloadCard audience="kids" />
              <AppDownloadCard audience="parents" />
            </div>
          </section>

          <section className="settings-section settings-help">
            <p className="font-bold text-gray-900">Help &amp; Tour</p>
            <p className="mt-1 text-sm text-gray-600">
              Take another look at the important parts of your dashboard.
            </p>
            <Button
              variant="secondary"
              className="mt-3 w-full"
              onClick={onReplayTour}
              disabled={saving}
            >
              Replay Dashboard Tour
            </Button>
          </section>


        </div>

      </div>
    </div>
  );
};


const MatchPiggyBankModal = ({
  open,
  currentAmount,
  currency,
  onClose,
  onMatched,
}) => {
  const [countedAmount, setCountedAmount] = useState('0.00');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const wasOpen = useRef(false);

  useEffect(() => {
    // Initialize once per opening. A balance refresh after matching must
    // not clear the success state or replace an amount being entered.
    if (open && !wasOpen.current) {
      setCountedAmount((Number(currentAmount || 0) / 100).toFixed(2));
      setError('');
      setResult(null);
    }
    wasOpen.current = open;
  }, [currentAmount, open]);

  if (!open) return null;

  const countedCents = parseCurrency(countedAmount);
  const difference = countedCents - Number(currentAmount || 0);

  const submit = async () => {
    const rawAmount = Number(countedAmount);
    if (
      countedAmount === '' ||
      !Number.isFinite(rawAmount) ||
      rawAmount < 0
    ) {
      setError('Enter an amount of $0.00 or more.');
      return;
    }

    setSaving(true);
    setError('');
    try {
      const response = await db.matchMyKidPiggyBank(countedCents);
      setResult(response);
      await onMatched?.();
    } catch (matchError) {
      setError(matchError?.message || 'Unable to match your Piggy Bank.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mypiggy-brand kid-match fixed inset-0 z-[160] flex items-center justify-center bg-black/50 px-4 py-6 overflow-y-auto">
      <div role="dialog" aria-modal="true" className="w-full max-w-md max-h-full overflow-y-auto rounded-2xl bg-white p-4 sm:p-5 shadow-2xl">
        <h2 className="text-2xl font-extrabold text-gray-900">Match My Piggy Bank</h2>
        <p className="mt-2 text-base font-bold leading-relaxed text-slate-600">
          Count the real money in your Piggy Bank. Enter how much you have, and MyPiggy will help make your balance match.
        </p>

        {result ? (
          <div className="mt-5 rounded-xl bg-green-50 p-4 text-green-900">
            <p className="font-bold">
              {result.approval_status === 'pending'
                ? 'Sent to your parent or guardian!'
                : 'Your Piggy Bank is matched! 🎉'}
            </p>
            <p className="mt-1 text-sm">
              {result.approval_status === 'pending'
                ? 'Your balance will update after the added money is approved.'
                : 'MyPiggy now matches the money you counted.'}
            </p>
          </div>
        ) : (
          <>
            <div className="mt-5 rounded-xl bg-purple-50 p-4">
              <p className="text-sm text-purple-700">MyPiggy currently shows</p>
              <p className="text-2xl font-extrabold text-purple-900">{formatCurrency(currentAmount, currency)}</p>
            </div>

            <div className="mt-4">
              <label className="mb-2 block text-sm font-bold text-gray-800" htmlFor="counted-piggy-bank-money">
                Money I counted
              </label>
              <input
                id="counted-piggy-bank-money"
                type="number"
                min="0"
                step="0.01"
                value={countedAmount}
                onChange={(event) => setCountedAmount(event.target.value)}
                className="w-full rounded-xl border border-gray-300 px-4 py-3 text-lg focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-200"
              />
            </div>

            <div className="mt-4 rounded-xl border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900">
              {difference > 0 && `MyPiggy will add ${formatCurrency(difference, currency)}.`}
              {difference < 0 && `MyPiggy will subtract ${formatCurrency(Math.abs(difference), currency)}.`}
              {difference === 0 && 'Great! The amounts already match.'}
            </div>
          </>
        )}

        {error && <p className="mt-3 text-sm text-red-600">{error}</p>}

        <div className={`mt-5 grid gap-3 ${result ? 'grid-cols-1' : 'grid-cols-2'}`}>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            {result ? 'Close' : 'Cancel'}
          </Button>
          {!result && (
            <Button onClick={submit} disabled={saving || countedAmount === ''}>
              {saving ? 'Matching...' : 'Match My Balance'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};


// ==================================================
// Kid Layout
// ==================================================

const KidLayout = ({
  children,
  title,
  showBack = false,
  onBack,
  avatar = '🧒',
  hideKidHeader = false,
  className = '',
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isNativeKidApp = Capacitor.isNativePlatform() && import.meta.env.VITE_APP_AUDIENCE === 'kids';
  const [keyboardOpen, setKeyboardOpen] = useState(false);

  useEffect(() => {
    if (!isNativeKidApp) return undefined;

    const viewport = window.visualViewport;
    const viewportHeight = () => Math.min(window.innerHeight, viewport?.height || window.innerHeight);
    let fullHeight = viewportHeight();
    let viewportWidth = window.innerWidth;

    const updateKeyboard = () => {
      // Reset the reference height after rotation; resizing for the keyboard
      // changes height while keeping the layout width the same.
      if (window.innerWidth !== viewportWidth) {
        viewportWidth = window.innerWidth;
        fullHeight = viewportHeight();
      }
      fullHeight = Math.max(fullHeight, viewportHeight());
      setKeyboardOpen(fullHeight - viewportHeight() > 120);
    };

    window.addEventListener('resize', updateKeyboard);
    viewport?.addEventListener('resize', updateKeyboard);
    return () => {
      window.removeEventListener('resize', updateKeyboard);
      viewport?.removeEventListener('resize', updateKeyboard);
    };
  }, [isNativeKidApp]);


  const {
    currentKid,
    kidFamily,
    refreshKid,
    logoutKid,
    isFamilyInactive,
    canWrite: familyAccessCanWrite,
  } = useKidAuth();


  const isKidWriteLocked =
    isFamilyInactive ||
    familyAccessCanWrite === false;


  const {
    balance,
    savingsBalance,
    loading: balanceLoading,
    error: balanceError,
    refresh: refreshBalance,
  } = useBalance(
    currentKid?.id,
    currentKid?.family_id,
    'kid'
  );


  const savedTheme =
    currentKid?.dashboard_theme ||
    'sunset';


  const [
    previewTheme,
    setPreviewTheme,
  ] = useState(savedTheme);

  const [
    previewAvatar,
    setPreviewAvatar,
  ] = useState(currentKid?.avatar || avatar);


  const [
    showSettings,
    setShowSettings,
  ] = useState(false);

  const [
    showMatchPiggyBank,
    setShowMatchPiggyBank,
  ] = useState(false);


  const [
    themeSaving,
    setThemeSaving,
  ] = useState(false);


  const [
    themeError,
    setThemeError,
  ] = useState('');


  const [
    settingsSummary,
    setSettingsSummary,
  ] = useState(null);


  const [
    settingsSummaryLoading,
    setSettingsSummaryLoading,
  ] = useState(false);


  const [
    settingsSummaryError,
    setSettingsSummaryError,
  ] = useState('');


  const [
    showTotalInfo,
    setShowTotalInfo,
  ] = useState(false);


  useEffect(() => {
    if (!showSettings) {
      setPreviewTheme(savedTheme);
      setPreviewAvatar(currentKid?.avatar || avatar);
    }
  }, [
    avatar,
    currentKid?.avatar,
    savedTheme,
    showSettings,
  ]);


  const themeConfig =
    getKidTheme(previewTheme);


  const piggyBankTotal =
    Number(balance || 0) +
    Number(savingsBalance || 0);


  const isAuthenticatedKid =
    Boolean(currentKid?.id);


  // --------------------------------------------------
  // Settings
  // --------------------------------------------------

  const loadSettingsSummary =
    async () => {
      setSettingsSummaryLoading(true);
      setSettingsSummaryError('');

      try {
        const summary =
          await db.getKidSettingsSummary();

        setSettingsSummary(
          summary
        );
      } catch (loadError) {
        diagnostic('error', 'kid.settings.load_failed');

        setSettingsSummaryError(
          loadError?.message ||
            'Unable to load your profile and family information.'
        );
      } finally {
        setSettingsSummaryLoading(false);
      }
    };


  const [focusSettingsApps, setFocusSettingsApps] = useState(false);

  const openSettings = (viewApps = false) => {
    setFocusSettingsApps(viewApps);
    setThemeError('');
    setSettingsSummaryError('');
    setPreviewTheme(savedTheme);
    setPreviewAvatar(currentKid?.avatar || avatar);
    setShowSettings(true);

    loadSettingsSummary();
  };


  useEffect(() => {
    if (!location.state?.viewKidApps) return;
    openSettings(true);
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.state, location.pathname, navigate]);


  const closeSettings = () => {
    if (themeSaving) return;

    setThemeError('');
    setPreviewTheme(savedTheme);
    setPreviewAvatar(currentKid?.avatar || avatar);
    setShowSettings(false);
  };


  const saveDashboardTheme =
    async () => {
      if (isKidWriteLocked) {
        setThemeError(
          'Theme changes are unavailable while this family is read-only.'
        );
        return;
      }
      try {

        setThemeSaving(true);
        setThemeError('');


        await db.updateDashboardTheme(
          previewTheme
        );

        await db.updateMyKidAvatar(
          previewAvatar
        );


        await refreshKid();


        setShowSettings(false);

      } catch (error) {

        diagnostic('error', 'kid.theme.save_failed');


        setThemeError(
          error?.message ||
            'Unable to save your theme. Please try again.'
        );

      } finally {

        setThemeSaving(false);

      }
    };


  // --------------------------------------------------
  // Navigation
  // --------------------------------------------------

  const handleBack = () => {

    if (onBack) {
      onBack();
      return;
    }


    navigate('/kid/dashboard');
  };


  // --------------------------------------------------
  // Header Identity
  // --------------------------------------------------

  const headerName =
    currentKid?.nickname ||
    currentKid?.full_name ||
    title ||
    'My Piggy Bank';


  const headerAvatar =
    currentKid?.avatar ||
    avatar;


  // ==================================================
  // Render
  // ==================================================

  return (
    <div
      className={`
        min-h-screen
        ${themeConfig.backgroundClass}
        ${className}
      `}
    >

      {!hideKidHeader && (

        <header
          className="
            sticky
            top-0
            z-40
            bg-white/95
            backdrop-blur-md
            shadow-lg
          "
          style={isNativeKidApp ? {
            paddingTop: 'env(safe-area-inset-top, 0px)',
          } : undefined}
        >

          <div
            className="
              max-w-md
              mx-auto
              px-2.5
              sm:px-4
              py-2.5
              sm:py-3
            "
          >

            <div
              className="
                flex
                items-center
                justify-between
                gap-1.5
                sm:gap-2.5
              "
            >


              {/* ====================================
                  Kid Identity / Back
                 ==================================== */}

              <div className="min-w-[6.25rem] flex-1">


                {showBack && isAuthenticatedKid ? (

                  <button
                    type="button"
                    onClick={handleBack}
                    aria-label="Back to dashboard"
                    title="Back to Dashboard"
                    className={`
                      max-w-full
                      ${isNativeKidApp ? 'w-full min-w-0' : ''}
                      min-h-10
                      sm:min-h-12
                      flex
                      items-center
                      gap-1.5
                      sm:gap-2
                      rounded-xl
                      border
                      border-gray-400
                      bg-gray-200
                      px-2.5
                      sm:px-3
                      text-gray-950
                      shadow-sm
                      hover:bg-gray-300
                      hover:border-gray-500
                      active:bg-gray-400
                      transition
                      focus:outline-none
                      focus:ring-2
                      focus:ring-gray-400
                    `}
                  >

                    <BackIcon
                      className="
                        w-4
                        h-4
                        sm:w-5
                        sm:h-5
                        flex-shrink-0
                      "
                    />


                    <span
                      className="
                        text-xl
                        sm:text-2xl
                        flex-shrink-0
                      "
                      aria-hidden="true"
                    >
                      {headerAvatar}
                    </span>


                    <span
                      className={`
                        text-sm
                        sm:text-base
                        font-extrabold
                        whitespace-normal
                        break-words
                        leading-tight
                        text-gray-950
                        ${isNativeKidApp ? 'min-w-0 flex-1 text-center [overflow-wrap:anywhere]' : ''}
                      `}
                    >
                      {headerName}
                    </span>

                  </button>

                ) : (

                  <div
                    className="
                      max-w-full
                      min-h-10
                      sm:min-h-12
                      flex
                      items-center
                      gap-2
                      sm:gap-2.5
                      rounded-xl
                      border
                      border-gray-300
                      bg-gray-100
                      px-2.5
                      sm:px-3
                      shadow-sm
                    "
                  >

                    <span
                      className="
                        text-xl
                        sm:text-2xl
                        flex-shrink-0
                      "
                      aria-hidden="true"
                    >
                      {headerAvatar}
                    </span>


                    <h1
                      className="
                        text-sm
                        sm:text-lg
                        font-extrabold
                        text-gray-950
                        whitespace-normal
                        break-words
                        leading-tight
                      "
                    >
                      {title?.startsWith('Hi,') ? title : `Hi, ${headerName}!`}
                    </h1>

                  </div>

                )}

              </div>


              {/* ====================================
                  Piggy Bank Total
                 ==================================== */}

              {isAuthenticatedKid && (

                <div
                  data-tour="kid-balance"
                  className="
                    relative
                    flex-shrink-0
                  "
                  onMouseEnter={() =>
                    setShowTotalInfo(true)
                  }
                  onMouseLeave={() =>
                    setShowTotalInfo(false)
                  }
                >

                  <div
                    className={`
                      h-10
                      sm:h-12
                      flex
                      items-center
                      gap-1.5
                      rounded-xl
                      border
                      px-2
                      shadow-sm
                      ${themeConfig.headerPillClass}
                    `}
                  >

                    <MyPiggyIcon name="my-piggy-bank" size="sm" />


                    <div className="leading-tight">


                      <p
                        className={`
                          text-sm
                          sm:text-base
                          font-extrabold
                          whitespace-nowrap
                          ${themeConfig.headerAmountClass}
                        `}
                      >

                        {balanceLoading
                          ? '...'
                          : balanceError
                          ? '--'
                          : formatCurrency(
                              piggyBankTotal
                            )}

                      </p>

                    </div>


                    <button
                      type="button"
                      aria-label="What is Piggy Bank Total?"
                      title="About Piggy Bank Total"
                      aria-expanded={showTotalInfo}
                      onFocus={() =>
                        setShowTotalInfo(true)
                      }
                      onBlur={() =>
                        setShowTotalInfo(false)
                      }
                      onClick={(event) => {

                        event.stopPropagation();


                        setShowTotalInfo(
                          (current) =>
                            !current
                        );
                      }}
                      className={`
                        w-5
                        h-5
                        sm:w-6
                        sm:h-6
                        rounded-full
                        border
                        flex
                        items-center
                        justify-center
                        text-[10px]
                        sm:text-[11px]
                        leading-none
                        font-extrabold
                        transition
                        focus:outline-none
                        focus:ring-2
                        ${themeConfig.headerInfoClass}
                      `}
                    >
                      i
                    </button>

                  </div>


                  {showTotalInfo && (

                    <div
                      role="tooltip"
                      className="
                        absolute
                        z-50
                        top-full
                        right-0
                        mt-3
                        w-64
                        max-w-[calc(100vw-2rem)]
                        rounded-xl
                        bg-gray-900
                        px-3
                        py-3
                        text-xs
                        font-normal
                        leading-relaxed
                        text-white
                        shadow-xl
                        pointer-events-none
                      "
                    >

                      <p>
                        All the money in your
                        piggy bank.
                      </p>


                      <p className="mt-2 font-semibold">

                        Available Money
                        {' + '}
                        Saved for Goals
                        {' = '}
                        Piggy Bank Total

                      </p>


                      <div
                        className="
                          absolute
                          -top-1
                          right-5
                          w-2
                          h-2
                          rotate-45
                          bg-gray-900
                        "
                      />

                    </div>

                  )}

                </div>

              )}


              {/* ====================================
                  Settings + Logout
                 ==================================== */}

              {isAuthenticatedKid && (

                <div
                  className="
                    flex
                    items-center
                    gap-1
                    sm:gap-2
                    flex-shrink-0
                  "
                >

                  {/* Settings */}

                  <button
                    data-tour="kid-settings"
                    type="button"
                    onClick={() => openSettings()}
                    aria-label="Open dashboard settings"
                    title="Settings"
                    className="
                      w-10
                      h-10
                      sm:w-12
                      sm:h-12
                      rounded-full
                      flex
                      items-center
                      justify-center
                      bg-gray-300
                      border
                      border-gray-500
                      text-gray-900
                      shadow-sm
                      hover:bg-gray-400
                      hover:border-gray-600
                      active:bg-gray-500
                      transition
                      focus:outline-none
                      focus:ring-2
                      focus:ring-gray-400
                    "
                  >

                    <MyPiggyIcon name="settings" size="sm" />

                  </button>


                  {/* Logout */}

                  <button
                    type="button"
                    onClick={logoutKid}
                    aria-label="Log out"
                    title="Log out"
                    className="
                      w-10
                      h-10
                      sm:w-12
                      sm:h-12
                      rounded-full
                      flex
                      items-center
                      justify-center
                      bg-red-100
                      border
                      border-red-400
                      text-red-700
                      shadow-sm
                      hover:bg-red-200
                      hover:border-red-500
                      hover:text-red-800
                      active:bg-red-300
                      transition
                      focus:outline-none
                      focus:ring-2
                      focus:ring-red-300
                    "
                  >

                    <LogoutIcon
                      className="
                        w-5
                        h-5
                        sm:w-6
                        sm:h-6
                      "
                    />

                  </button>

                </div>

              )}

            </div>

          </div>

        </header>

      )}


      {/* ========================================
          Main Page Content
         ======================================== */}

      <main className={`max-w-md mx-auto px-4 py-5 sm:py-6 ${isNativeKidApp && currentKid && !keyboardOpen ? "pb-28" : ""}`}>

        {isFamilyInactive && (
          <div
            role="status"
            aria-live="polite"
            className="
              mb-5
              rounded-2xl
              border-2
              border-red-300
              bg-red-50
              px-4
              py-4
              shadow-sm
            "
          >
            <div className="flex items-start gap-3">

              <MyPiggyIcon name="locked-read-only" size="md" />

              <div>
                <p className="font-extrabold text-red-900">
                  This family is currently read-only.
                </p>

                <p className="mt-1 text-sm leading-relaxed text-red-800">
                  You can still look at your money,
                  savings, chores, and reports, but
                  you can&apos;t make changes right now.
                </p>
              </div>

            </div>
          </div>
        )}


        <motion.div
          initial={{
            opacity: 0,
            scale: 0.95,
          }}
          animate={{
            opacity: 1,
            scale: 1,
          }}
          transition={{
            duration: 0.3,
          }}
        >
          {children}
        </motion.div>

      </main>


      {isNativeKidApp && currentKid && !keyboardOpen && (
        <nav aria-label="Kids app" className="fixed inset-x-0 bottom-0 z-40 border-t border-purple-200 bg-white/95 shadow-lg backdrop-blur">
          <div className="mx-auto grid max-w-md grid-cols-4 gap-1 px-2 pt-2 pb-[max(0.5rem,env(safe-area-inset-bottom))]">
            {[
              { path: '/kid/dashboard', label: 'Home', icon: 'my-piggy-bank' },
              { path: '/kid/chores', label: 'Chores', icon: 'do-chores' },
              { path: '/kid/savings', label: 'Savings', icon: 'my-savings' },
              { path: '/kid/reports', label: 'Activity', icon: 'transaction-history' },
            ].map(({ path, label, icon }) => {
              const selected = location.pathname === path;
              return (
                <button
                  key={path}
                  type="button"
                  onClick={() => navigate(path)}
                  aria-current={selected ? 'page' : undefined}
                  className={`flex min-h-14 flex-col items-center justify-center gap-1 rounded-xl px-1 py-1 text-center text-xs font-bold ${selected ? 'bg-purple-100 text-purple-900' : 'text-gray-600'}`}
                >
                  <MyPiggyIcon name={icon} size="sm" loading="eager" />
                  {label}
                </button>
              );
            })}
          </div>
        </nav>
      )}

      {/* ========================================
          Settings Modal
         ======================================== */}

      <KidSettingsModal
        open={showSettings}
        focusApps={focusSettingsApps}
        summary={settingsSummary}
        loadingSummary={
          settingsSummaryLoading
        }
        summaryError={
          settingsSummaryError
        }
        isNativeKidApp={isNativeKidApp}
        selectedTheme={previewTheme}
        onSelectTheme={setPreviewTheme}
        selectedAvatar={previewAvatar}
        onSelectAvatar={setPreviewAvatar}
        onReplayTour={() => {
          setShowSettings(false);
          navigate('/kid/dashboard', {
            state: {
              replayKidTour: true,
            },
          });
        }}
        onMatchPiggyBank={() => {
          setShowSettings(false);
          setShowMatchPiggyBank(true);
        }}
        onSave={saveDashboardTheme}
        onClose={closeSettings}
        saving={themeSaving}
        error={themeError}
        isFamilyInactive={
          isKidWriteLocked
        }
      />

      <MatchPiggyBankModal
        open={showMatchPiggyBank}
        currentAmount={piggyBankTotal}
        currency={kidFamily?.currency || 'USD'}
        onClose={() => setShowMatchPiggyBank(false)}
        onMatched={refreshBalance}
      />

    </div>
  );
};


export default KidLayout;


